// Series documents plus override documents, folded into the dated lessons a
// window actually contains — by the shared core.
//
// `recurrence.js` steps a single rule; this is the composition on top of it —
// what an override does to the occurrence its rule produces, and which
// occurrences belong in a window once moves are taken into account.
//
// The fold is `core/src/schedule.rs` and the projection the conflict check
// compares is `coda_domain::conflicts::schedules_of` (#214); this file is the
// shaping either side of the crossing — documents into the boundary's five
// joined lists, flat answers back into the nested shapes the app reads.
//
// ── Eight fields `golden/schedule.json` does not see ────────────────────────
//
// The occurrence carries `movedDate`, `locationId`, `visibility`,
// `requiresMakeupCredit`, `allowOnlineBooking`, the two descriptions and
// `recurrenceMode`, each resolved by a precedence, and the calendar draws all
// eight. `golden/schedule.json` records only eleven keys and is silent about
// these; their vectors are `core/src/schedule_vectors.json`.
//
// ── `pendingWrite` stays in JavaScript ─────────────────────────────────────
//
// `pendingWrite` is `Boolean(s.pendingWrite || ov?.pendingWrite)` over the
// `d.metadata.hasPendingWrites` flag `useEvents.jsx` stamps onto each
// document. It is a fact about this host's write queue, not about a lesson, so
// it is rejoined here after the fold.
//
// `allDay` is the core's rule (#160, #312): override-wins-else-series, the same
// precedence as `requiresMakeupCredit`, at `core/src/schedule.rs`'s `all_day:`
// line and pinned by its `mod all_day`. `flattenDocuments` passes it in and
// the occurrence carries it out like every other field; nothing rejoins it
// here.
//
// ── Two states: removed, or cancelled ──────────────────────────────────────
//
// An occurrence of a series is either there or not there. MMS has no
// cancelled-occurrence state: Delete removes the lesson, and cancelling is an
// attendance status (`Teacher Cancelled`, Not Billable, one of the eight in
// `core/src/attendance.rs`), undone by setting it back to `Unrecorded`.
//
// One date of a recurring rule cannot simply be deleted, since the next
// expansion would produce it again, so:
//
//   **removed**    the rule date is in the series' `exceptions` array, which
//                  the expansion skips. Its override document is deleted in
//                  the same write: an override keyed to a date the rule no
//                  longer reaches is unreachable, the rule
//                  `dropOverridesFrom` applies to a shortened run.
//
//   **cancelled**  not a property of the occurrence. The lesson is still
//                  produced and an attendance record says it was called off.
//                  It still occupies its slot for the conflict check, as MMS
//                  still draws it.
//
// So this fold neither reads nor emits `cancelled`; a document that still
// carries the field is drawn as an ordinary lesson (#130).

// `./core.js`, not `../lib/core.js`: this directory is copied verbatim into
// functions/domain/ and the door is inside the copy (#218).
import { coreSync } from './core.js';

// ── Shaping documents into the boundary's five joined lists ─────────────────
//
// The core takes the calendar as a join: series, their exceptions, their
// students, overrides, and overrides' students, each a flat list keyed back to
// its parent. A stored series holds the same data nested (`exceptions` and
// `studentIds` are arrays on the document). `src/domain/conflicts.js` has the
// same flattener over the *schedule* shape.
//
// Whether an override is identified well enough to join a series
// (`seriesId && date`) decides whether a document participates in the
// calendar at any host, so it is a rule, not shaping: `coreSync().overrideKey`
// (#214).

/** A typed number for the boundary, or null for an absent or unreadable one. */
const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * A typed boolean for the boundary, or null for an absent one.
 *
 * `null` and `false` are different answers here and the difference is the whole
 * precedence: an override with no `requiresMakeupCredit` inherits the series',
 * one that sets it to `false` overrides it. A document holding `0` or `""`
 * means `false` rather than "absent", which is why this is not simply
 * `?? null`.
 */
const bool = (v) => (v === null || v === undefined ? null : Boolean(v));

/**
 * A whole number for the boundary, or null. Maximum Students (#766) crosses as
 * an integer, and a stored fraction — which no writer here makes — reads as
 * absent, so MMS's 1, rather than refusing the whole window.
 */
const whole = (v) => {
  const n = num(v);
  return Number.isInteger(n) ? n : null;
};

/**
 * The join key for an override document that carries no id of its own.
 *
 * `overrideKey` is the core's rule about whether an override is identified
 * well enough to belong to a series at all, and `occurrenceId` is the id every
 * writer stamps from those same two fields. An override that names neither
 * gets `''`: unidentifiable, and grouped with the others of its kind.
 */
function overrideJoinKey(o) {
  const key = coreSync().overrideKey(o.seriesId ?? null, o.date ?? null);
  return key ? coreSync().occurrenceId(key.seriesId, key.date) : '';
}

/**
 * The documents, split into the five lists and the rows they came from.
 *
 * `kept` is the series documents in row order, so a caller rejoining a per-
 * series answer can pair them off without matching on id.
 */
function flattenDocuments(series, overrides) {
  const rows = [];
  const kept = [];
  const exceptions = [];
  const seriesStudents = [];
  const overrideRows = [];
  const overrideStudents = [];

  for (const s of series || []) {
    if (!s) continue;
    const id = s.id ?? '';
    kept.push(s);
    rows.push({
      id,
      startDate: s.startDate ?? null,
      startTime: s.startTime ?? null,
      durationMin: num(s.durationMin),
      frequency: s.frequency ?? null,
      interval: num(s.interval),
      endDate: s.endDate ?? null,
      byWeekday: num(s.byWeekday),
      weekOfMonth: num(s.weekOfMonth),
      categoryId: s.categoryId ?? null,
      categoryLabel: s.categoryLabel ?? null,
      pricingRule: s.pricingRule ?? null,
      priceOverrideMinor: num(s.priceOverrideMinor),
      locationId: s.locationId ?? null,
      visibility: s.visibility ?? null,
      requiresMakeupCredit: bool(s.requiresMakeupCredit),
      allowOnlineBooking: bool(s.allowOnlineBooking),
      // MMS's "All day" checkbox (#312, #160). `bool()` rather than `Boolean()`
      // because the absence is the whole precedence: `null` inherits, `false`
      // is an answer and clears.
      allDay: bool(s.allDay),
      publicDescription: s.publicDescription ?? null,
      privateDescription: s.privateDescription ?? null,
      recurrenceMode: s.recurrenceMode ?? null,
      // MMS's Maximum Students (#766), written only while online booking is
      // on; absent reads as 1 in the fold.
      maxStudents: whole(s.maxStudents),
    });
    for (const d of s.exceptions || []) {
      if (typeof d === 'string' && d) exceptions.push({ seriesId: id, date: d });
    }
    for (const sid of s.studentIds || []) {
      if (sid) seriesStudents.push({ seriesId: id, studentId: sid });
    }
  }

  for (const o of overrides || []) {
    if (!o) continue;
    // ── An override with no id (#296) ───────────────────────────────────────
    //
    // Two id-less overrides both keyed `""` would be grouped by
    // `by_override_student` in `core-ffi/src/lib.rs`, and each would come back
    // carrying the union of their students. A stored override always has an
    // id; a hand-built pseudo-document does not
    // (`test/integration/reminders.test.jsx`, and the reminder worker's path
    // runs through this same join, #249).
    //
    // So an id-less override is keyed by what identifies an override anyway:
    // `override_key` in `core/src/schedule.rs` names its series and rule date,
    // and `occurrences_between` looks overrides up by
    // `ids::occurrence_id(seriesId, ruleDate)`, the id a stored document
    // carries. Only when the id is missing: re-deriving every time would add
    // two crossings per override per window and change nothing for a document
    // this app wrote.
    const oid = o.id || overrideJoinKey(o);
    overrideRows.push({
      id: oid,
      seriesId: o.seriesId ?? null,
      date: o.date ?? null,
      movedDate: o.movedDate ?? null,
      startTime: o.startTime ?? null,
      durationMin: num(o.durationMin),
      categoryId: o.categoryId ?? null,
      categoryLabel: o.categoryLabel ?? null,
      pricingRule: o.pricingRule ?? null,
      priceOverrideMinor: num(o.priceOverrideMinor),
      locationId: o.locationId ?? null,
      visibility: o.visibility ?? null,
      requiresMakeupCredit: bool(o.requiresMakeupCredit),
      allowOnlineBooking: bool(o.allowOnlineBooking),
      // The override's half of that precedence: silent inherits the series',
      // `false` clears it for this date alone. See the series row above.
      allDay: bool(o.allDay),
      publicDescription: o.publicDescription ?? null,
      privateDescription: o.privateDescription ?? null,
      maxStudents: whole(o.maxStudents),
      // The load-bearing flag: an override with no `studentIds` inherits the
      // series', one with an empty list is a lesson with nobody in it, and a
      // join table contributes zero rows for both.
      setsStudentIds: Array.isArray(o.studentIds),
    });
    for (const sid of o.studentIds || []) {
      if (sid) overrideStudents.push({ overrideId: oid, studentId: sid });
    }
  }

  return { rows, kept, exceptions, seriesStudents, overrideRows, overrideStudents };
}

/** The five lists as the boundary's positional arguments. */
const args = (f) => [f.rows, f.exceptions, f.seriesStudents, f.overrideRows, f.overrideStudents];

// Expand every series across a window and return flat, dated occurrences.
//
// A lesson's attendees are a list inside a record, which the flat boundary
// refuses, so it splits: `occurrencesBetween` returns the lessons and
// `occurrenceAttendees` returns who is in them, over one shared fold. They are
// rejoined here once rather than in every screen, the same shape
// `Schedule.between` takes on Android.
//
// There is no override index to memoise: every override is kept, and moved
// ones are filtered only on having a `movedDate`, never on the window, so an
// index could not reduce what crosses. Memoisation belongs on the result,
// keyed by window.
export function occurrencesBetween(series, overrides, startISO, endISO) {
  const f = flattenDocuments(series, overrides);
  const a = args(f);
  const lessons = coreSync().occurrencesBetween(...a, startISO, endISO);
  const attendees = coreSync().occurrenceAttendees(...a, startISO, endISO);

  // Grouped once rather than filtered per lesson: a studio with a full week in
  // the window would otherwise be quadratic in attendees.
  const byOccurrence = {};
  for (const { occurrenceId, studentId } of attendees) {
    (byOccurrence[occurrenceId] ||= []).push(studentId);
  }

  // `pendingWrite` is the field the fold does not answer — see the header.
  const pendingSeries = {};
  for (const s of f.kept) if (s.pendingWrite) pendingSeries[s.id ?? ''] = true;
  const pendingOverride = {};
  for (const o of overrides || []) if (o?.pendingWrite) pendingOverride[o.id ?? ''] = true;

  // ── `null` where the fold answered `null`, `undefined` where it did not ──
  //
  // An absent `Option` arrives from the boundary as `undefined`, and for these
  // four fields `null` is the answer for "nothing supplied one". `startTime` is
  // deliberately not among them: it is `ov?.startTime || s.startTime` with no
  // fallback of its own, so a series with no start time produces `undefined`,
  // and a `null` here would be a shape nobody wrote.
  return lessons.map((o) => ({
    ...o,
    movedDate: o.movedDate ?? null,
    locationId: o.locationId ?? null,
    categoryId: o.categoryId ?? null,
    priceOverrideMinor: o.priceOverrideMinor ?? null,
    studentIds: byOccurrence[o.id] || [],
    // A Firestore fact carried here because the fold is the only place a
    // series' pending state and its override's meet. The data layer stamps it
    // onto the documents it passes in.
    pendingWrite: Boolean(pendingSeries[o.seriesId] || pendingOverride[o.id]),
  }));
}

// Every series as a *schedule* — the rule plus the per-entry deviations —
// which is the shape domain/conflicts.js compares.
//
// Built here from the raw documents because the conflict check must see the
// same fold the calendar draws: a removed entry is not produced at all, and a
// moved one is taught on the day it moved to.
//
// The projection is `coda_domain::conflicts::schedules_of`. Its defaults are
// why it is not shaping: a check handed the raw documents would name a lesson
// with no category and expand a series with no frequency as nothing at all.
//
// It comes back in three lists, since a record inside a record is what the
// flat boundary refuses, and is rejoined here. `exceptions` and `studentIds`
// are not among them: the projection copies both verbatim off the document.
export function schedulesOf(series, overrides) {
  const f = flattenDocuments(series, overrides);
  const a = args(f);
  const rules = coreSync().schedulesOf(...a);
  const overrideRows = coreSync().scheduleOverrides(...a);
  const overrideStudents = coreSync().scheduleOverrideStudents(...a);

  // ` ` because a rule date cannot contain one and a series id should not.
  const key = (seriesId, ruleDate) => `${seriesId} ${ruleDate}`;
  const studentsByEntry = {};
  for (const r of overrideStudents) {
    (studentsByEntry[key(r.seriesId, r.ruleDate)] ||= []).push(r.studentId);
  }

  // Insertion order is load-bearing: the rows arrive in the projection's own
  // order, per series, and a string-keyed object keeps assignment order.
  // `occurrencesOf` walks this map for lessons moved into a window and appends
  // what it finds to the expansion in that order.
  const overridesBySeries = {};
  for (const r of overrideRows) {
    const map = (overridesBySeries[r.seriesId] ||= {});
    map[r.ruleDate] = {
      movedDate: r.movedDate ?? null,
      startTime: r.startTime ?? null,
      durationMin: r.durationMin ?? null,
      categoryId: r.categoryId ?? null,
      categoryLabel: r.categoryLabel ?? null,
      // Absent rather than empty where the document set no `studentIds`: the
      // same distinction `setsStudentIds` carries in the other direction.
      ...(r.setsStudentIds
        ? { studentIds: studentsByEntry[key(r.seriesId, r.ruleDate)] || [] }
        : {}),
    };
  }

  return rules.map((r, i) => ({
    seriesId: r.seriesId ?? null,
    startDate: r.startDate ?? null,
    // `?? null` throughout, because an absent `Option` crosses as `undefined`.
    // That includes `startTime`: a schedule is only ever read by
    // `occurrencesOf` and the conflict flattener, both of which spell it
    // `?? null` on the way in, so the two are the same answer here.
    startTime: r.startTime ?? null,
    durationMin: r.durationMin ?? null,
    frequency: r.frequency ?? null,
    interval: r.interval ?? null,
    endDate: r.endDate ?? null,
    exceptions: f.kept[i]?.exceptions || [],
    byWeekday: r.byWeekday ?? null,
    weekOfMonth: r.weekOfMonth ?? null,
    categoryId: r.categoryId ?? null,
    categoryLabel: r.categoryLabel ?? null,
    studentIds: f.kept[i]?.studentIds || [],
    overrides: overridesBySeries[r.seriesId] || {},
  }));
}
